import { notyf } from '@modules';

class FileUpload extends HTMLElement {
  connectedCallback() {
    this.selectedFiles = [];

    this.fileInput = this.querySelector('input[type="file"]');
    this.button = this.querySelector('[data-file-button]');
    this.dropzone = this.querySelector('[data-file-dropzone]');
    this.fileList = this.querySelector('[data-file-list]');
    this.fileItemTemplate = this.querySelector('[data-file-item-template]');

    this.maxSize = parseInt(this.dataset.maxSize || '5') * 1024 * 1024; // Convert MB to bytes
    this.acceptedTypes = (this.dataset.accept || 'image/*').split(',').map((t) => t.trim());

    this.handlers = {};
    this.attachEventListeners();
  }

  attachEventListeners() {
    // Create stable references
    this.handlers.click = () => this.fileInput.click();
    this.handlers.change = (e) => this.handleFiles(e.target.files);
    this.handlers.dragOver = (e) => this.handleDragOver(e);
    this.handlers.drop = (e) => this.handleDrop(e);
    this.handlers.dragLeave = (e) => this.handleDragLeave(e);
    this.handlers.dragEnter = (e) => this.handleDragEnter(e);

    // Attach listeners
    this.button.addEventListener('click', this.handlers.click);
    this.fileInput.addEventListener('change', this.handlers.change);

    this.dropzone.addEventListener('dragover', this.handlers.dragOver);
    this.dropzone.addEventListener('drop', this.handlers.drop);
    this.dropzone.addEventListener('dragleave', this.handlers.dragLeave);
    this.dropzone.addEventListener('dragenter', this.handlers.dragEnter);
  }

  handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropzone.style.opacity = '0.5';
  }

  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropzone.style.opacity = '0.5';
  }

  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropzone.style.opacity = '1';
  }

  handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropzone.style.opacity = '1';

    const files = e.dataTransfer.files;
    this.handleFiles(files);
  }

  handleFiles(files) {
    const filesArray = Array.from(files);

    filesArray.forEach((file) => {
      if (this.validateFile(file)) {
        this.selectedFiles.push(file);
      }
    });

    this.renderFileList();
  }

  validateFile(file) {
    const isAccepted = this.acceptedTypes.some((type) => {
      if (type === 'image/*') {
        return file.type.startsWith('image/');
      }
      if (type === 'video/*') {
        return file.type.startsWith('video/');
      }
      return file.type === type;
    });

    if (!isAccepted) {
      notyf.error(`Invalid file type: ${file.name}`);
      return false;
    }

    if (file.size > this.maxSize) {
      const maxSizeMB = this.maxSize / (1024 * 1024);
      notyf.error(`File too large: ${file.name} (max ${maxSizeMB}MB)`);
      return false;
    }

    return true;
  }

  renderFileList() {
    this.fileList.innerHTML = '';

    this.selectedFiles.forEach((file, index) => {
      const clone = this.fileItemTemplate.content.cloneNode(true);

      const fileName = clone.querySelector('[data-file-name]');
      fileName.textContent = file.name;

      const removeButton = clone.querySelector('[data-remove-file]');
      removeButton.addEventListener('click', () => this.removeFile(index));

      this.fileList.appendChild(clone);
    });
  }

  removeFile(index) {
    this.selectedFiles.splice(index, 1);
    this.renderFileList();
  }

  getFiles() {
    return this.selectedFiles;
  }

  clearFiles() {
    this.selectedFiles = [];
    this.fileInput.value = '';
    this.renderFileList();
  }

  disableInput() {
    if (this.fileInput) {
      this.fileInput.disabled = true;
    }
  }

  disconnectedCallback() {
    this.button.removeEventListener('click', this.handlers.click);
    this.fileInput.removeEventListener('change', this.handlers.change);

    this.dropzone.removeEventListener('dragover', this.handlers.dragOver);
    this.dropzone.removeEventListener('drop', this.handlers.drop);
    this.dropzone.removeEventListener('dragleave', this.handlers.dragLeave);
    this.dropzone.removeEventListener('dragenter', this.handlers.dragEnter);
  }
}

customElements.define('file-upload', FileUpload);
